import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { SHARED_ENVIRONMENT, SharedEnvironment } from '@sd-portal/shared/environment';
import { SessionStorageKeys } from '@sd-portal/shared/models';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class OidcGuard {
  constructor(
    private readonly router: Router,
    private readonly oidcSecurityService: OidcSecurityService,
    @Inject(SHARED_ENVIRONMENT) private readonly environment: SharedEnvironment
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.getAuthStatus(state.url);
  }

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.getAuthStatus(state.url);
  }

  canLoad(route: Route): Observable<boolean> {
    const url = '/' + route.path;
    return this.getAuthStatus(url);
  }

  private getAuthStatus(url: string = '/'): Observable<boolean> {
    return this.oidcSecurityService.checkAuth().pipe(
      switchMap((authResponse: LoginResponse) => {
        if (authResponse.isAuthenticated) {
          return of(true);
        }

        return this.oidcSecurityService.forceRefreshSession().pipe(
          switchMap((refreshResponse: LoginResponse) => {
            if (refreshResponse.isAuthenticated) {
              this.oidcSecurityService.getAccessToken().subscribe(token => {
                window.sdPortalAuthToken = token;
              });
              return of(true);
            } else {
              return this.redirectToUnauthorized(url);
            }
          }),
          catchError(() => {
            return this.redirectToUnauthorized(url);
          })
        );
      })
    );
  }

  private redirectToUnauthorized(url: string): Observable<boolean> {
    if (url) {
      sessionStorage.setItem(SessionStorageKeys.REDIRECT_URL, url);
    }
    this.router.navigate([this.environment.oidcsettings.unauthorized_route]);
    return of(false);
  }
}
